import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ContractApiService } from "src/app/core/api/contract-api.service";
import { concatAll, forkJoin, of, switchMap } from "rxjs";
import {
    ClientSideRowModelModule,
    ModuleRegistry,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    ColDef,
} from "ag-grid-community";
import { AgGridAngular } from "ag-grid-angular";
import { formatDate, JsonPipe, NgClass } from "@angular/common";
import { ContractSmall } from "src/app/core/models/contract-small";
import { ContractCommentsComponent } from "./components/contract-comments/contract-comments.component";
import { SowComponent } from "./components/sow/sow.component";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
]);

@Component({
    selector: "app-contract-profile",
    standalone: true,
    imports: [
        TranslatePipe,
        AgGridAngular,
        ContractCommentsComponent,
        SowComponent,
        NgClass
    ],
    templateUrl: "./contract-profile.component.html",
    styleUrl: "./contract-profile.component.scss",
})
export class ContractProfileComponent implements OnInit {
    public contractTableTitles: any = {};
    contractId: number;
    public contractsByProfileAndCustomer: ContractSmall[] = [];
    public contractByProfile: ContractSmall[] = [];
    public colDefs: ColDef<ContractSmall>[] = [];
    public colDefsByProgileId: ColDef<ContractSmall>[] = [];
    public showTab: "Sow" | "Comments" = "Sow";
    public consultantName: string;
    public customerName: string;
    public taskTitle: string;

    constructor(
        private contractApiSerivice: ContractApiService,
        private route: ActivatedRoute,
        private router: Router,
        private translateService: TranslateService
    ) {}

    defaultColDef = {
        sortable: false,
        filter: false,
        resizable: false,
        floatingFilter: false,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
        suppressColumnVirtualisation: true,
        suppressSizeToFit: true,
        suppressMovable: true,
    };

    ngOnInit(): void {
        this.contractId = Number(
            this.route.snapshot.paramMap.get("contractId")
        );
        this.translateService.stream("contracts").subscribe((res) => {
            this.contractTableTitles = res;
            this.initializeColumnDefs();
            this.initializeProfileInfoColumnDefs();
        });

        this.loadContractDetails(this.contractId);
    }

    public loadContractDetails(contractId: number) {
        if (!contractId) return;

        this.contractApiSerivice
            .getContractByContractId(contractId)
            .pipe(
                switchMap((contract) => {
                    this.consultantName = contract.fullname;
                    this.customerName = contract.customerName;
                    this.taskTitle = contract.taskSmall.title;

                    return forkJoin({
                        contractsByProfileAndCustomer: contract.customerId
                            ? this.contractApiSerivice.getContractsByProfileAndCustomer(
                                  contract.profileId,
                                  contract.customerId
                              )
                            : of([]),
                        contractByProfile:
                            this.contractApiSerivice.getContracts(
                                contract.profileId
                            ),
                    });
                })
            )
            .subscribe({
                next: (result) => {
                    this.contractsByProfileAndCustomer =
                        result.contractsByProfileAndCustomer.filter(
                            (c) => c.id !== this.contractId
                        );
                    this.contractByProfile = result.contractByProfile.filter(
                        (c) => c.id !== this.contractId
                    );
                },
                error: (error) => {},
            });
    }

    public onRowClicked(event: { data: { id: any } }) {
        const navigateToContractId = event.data.id;
        this.contractId = navigateToContractId;
        // Navigate to a dummy route first, then to the target
        this.router
            .navigateByUrl("/", { skipLocationChange: true })
            .then(() => {
                this.router.navigate([
                    "contract",
                    navigateToContractId,
                    "details",
                ]);
            });
    }

    private initializeColumnDefs() {
        this.colDefs = [
            {
                field: "taskTitle",
                headerName: this.contractTableTitles.title,
                filter: false,
                flex: 1,
                resizable: true,
            },
            {
                field: "startDate",
                headerName: this.contractTableTitles.startDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 115,
                sort: "desc",
            },
            {
                field: "endDate",
                headerName: this.contractTableTitles.endDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 120,
            },
            {
                field: "purchasePrice",
                width: 120,
                headerName: this.contractTableTitles.purchasePrice,
            },
            {
                field: "soldPrice",
                width: 120,
                headerName: this.contractTableTitles.soldPrice,
            },
        ];
    }

    public goToTab(showTab: "Sow" | "Comments") {
        this.showTab = showTab;
    }

    private initializeProfileInfoColumnDefs() {
        this.colDefsByProgileId = [
            {
                field: "customer",
                headerName: this.contractTableTitles.customerName,
                filter: false,
                flex: 1,
                resizable: true
            },
            {
                field: "taskTitle",
                headerName: this.contractTableTitles.title,
                flex: 1,
                resizable: true
            },
            {
                field: "startDate",
                headerName: this.contractTableTitles.startDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 115,
                sort: "desc",
            },
            {
                field: "endDate",
                headerName: this.contractTableTitles.endDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 120,
            },
            {
                field: "purchasePrice",
                width: 120,
                headerName: this.contractTableTitles.purchasePrice,
            },
            {
                field: "soldPrice",
                width: 120,
                headerName: this.contractTableTitles.soldPrice,
            },
        ];
    }
    public goBack() {
        this.router.navigate(["contracts-overview"]);
    }
}
